import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import KpiCard from '../components/KpiCard';
import { ShieldCheck, DollarSign, AlertCircle, Users as UsersIcon, CheckCircle2 } from 'lucide-react';
import { mrr } from '../data/mockData';

export default function MaintenancePlans() {
  const { plans, customers } = useApp();

  const stats = useMemo(() => {
    const active = customers.length;
    const monthly = mrr(customers, plans);
    const expiringSoon = Math.round(customers.length * 0.12);
    const missed = 3;
    const noPlan = 18;
    return { active, monthly, expiringSoon, missed, noPlan };
  }, [customers, plans]);

  const byPlan = useMemo(() => {
    return plans.map((p) => ({
      ...p,
      count: customers.filter((c) => c.planId === p.id).length,
    }));
  }, [plans, customers]);

  const needPlan = customers.filter((_, i) => i % 5 === 1).slice(0, 6);

  return (
    <div className="space-y-6" data-testid="plans-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Maintenance Plans</div>
        <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Recurring revenue & plan coverage</h1>
        <p className="mt-1 text-sm text-slate-500">Protect comfort, lock in service revenue, and reduce emergency dispatches.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Active Plans" value={stats.active} icon={ShieldCheck} accent="emerald" testId="kpi-plans-active" />
        <KpiCard label="MRR" value={`$${stats.monthly.toLocaleString()}`} icon={DollarSign} accent="blue" testId="kpi-plans-mrr" />
        <KpiCard label="Expiring Soon" value={stats.expiringSoon} icon={AlertCircle} accent="amber" testId="kpi-plans-expiring" />
        <KpiCard label="Missed Renewals" value={stats.missed} icon={AlertCircle} accent="red" testId="kpi-plans-missed" />
        <KpiCard label="No-Plan Customers" value={stats.noPlan} icon={UsersIcon} accent="indigo" testId="kpi-plans-noplan" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="plan-tiers">
        {byPlan.map((p) => (
          <Card key={p.id} className="p-5 border-slate-200 shadow-sm flex flex-col">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{p.tier.split(' ')[0]}</div>
                <div className="mt-1 text-lg font-semibold text-slate-900 leading-tight">{p.tier}</div>
              </div>
              <StatusBadge status={p.color} />
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-mono text-3xl font-semibold text-slate-900">${p.priceMonthly}</span>
              <span className="text-xs text-slate-500">/mo</span>
            </div>
            <div className="mt-1 font-mono text-xs text-slate-500">{p.count} customers · ${p.count * p.priceMonthly}/mo</div>
            <ul className="mt-4 space-y-2 text-sm text-slate-700 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="mt-5">Manage Tier</Button>
          </Card>
        ))}
      </div>

      <Card className="p-5 border-slate-200 shadow-sm" data-testid="plan-opportunities">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Upsell Opportunities</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-900">Customers without an active plan</div>
          </div>
          <Button className="bg-[#0052CC] hover:bg-[#0043A6]">Launch Outreach Campaign</Button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {needPlan.map((c) => (
            <div key={c.id} className="p-4 rounded-md border border-slate-200 hover:border-slate-300 transition-colors">
              <div className="flex items-center justify-between">
                <div className="font-medium text-slate-900">{c.name}</div>
                <StatusBadge status={c.healthBand.toLowerCase()} />
              </div>
              <div className="mt-1 text-xs text-slate-500">{c.address}, {c.city}</div>
              <div className="mt-2 font-mono text-xs text-slate-500">Suggested: <span className="text-slate-800 font-semibold">Preventive Maintenance</span></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
